/**
 * The partner half of the review queue: its data, its two mutations, and the
 * two dialogs they need.
 *
 * Both halves of the queue are `require_admin` and single-approver
 * (`admin/registrations.py`, `admin/federated_partners.py`), so putting them in
 * one table changes where the decision happens, not who may make it. What
 * separates them is Kind, and what a decision does next.
 *
 * The risk the merge creates: approving a partner returns its master token in
 * the response body, shown exactly once. A queue is built for clearing items
 * quickly, which is precisely where a reveal-once value gets dismissed by
 * reflex. So approval opens a copy-once dialog that does not auto-advance.
 *
 * A hook rather than a component because the rows now live in the shared table
 * while the dialogs are page-level: this keeps the mutations next to the dialog
 * state they drive, and hands the page one node to render.
 */
import { ApiError } from '@optum/api-client/client';
import {
  approveFederatedPartner,
  listFederatedPartners,
  rejectFederatedPartner,
} from '@optum/api-client/endpoints/federated-partners';
import type { FederatedPartnerWithSecret } from '@optum/api-client/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { Button } from '../../ui/Button.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { useStepUp } from '../../lib/step-up.js';
import { Dialog } from '../../ui/Dialog.js';
import { PartnerTokenDialog } from '../federation/PartnerTokenDialog.js';

export function usePartnerReview() {
  const toast = useToast();
  const qc = useQueryClient();
  const [issued, setIssued] = useState<FederatedPartnerWithSecret | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  // Approval mints the master token, so the agent asks for a fresh sign-in.
  const [guardedApprove, approveStepUp] = useStepUp(approveFederatedPartner);

  const query = useQuery({
    queryKey: qk.federatedPartners(),
    queryFn: () => listFederatedPartners(),
  });

  const approve = useMutation({
    mutationFn: (id: string) => guardedApprove(id),
    onSuccess: (partner) => {
      qc.invalidateQueries({ queryKey: qk.federatedPartners() });
      // Deliberately no toast-and-move-on: the dialog is the only chance to
      // capture the token.
      setIssued(partner);
    },
    onError: (e) => toast.error('Approve failed', e instanceof ApiError ? e.message : String(e)),
  });

  const reject = useMutation({
    mutationFn: ({ id, why }: { id: string; why: string }) => rejectFederatedPartner(id, why),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.federatedPartners() });
      toast.success('Partner rejected');
      setRejecting(null);
      setReason('');
    },
    onError: (e) => toast.error('Reject failed', e instanceof ApiError ? e.message : String(e)),
  });

  const dialogs: ReactNode = (
    <>
      <PartnerTokenDialog issued={issued} onClose={() => setIssued(null)} />
      {approveStepUp}

      <Dialog
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        icon="block"
        tone="danger"
        title="Reject partner"
      >
        <label
          htmlFor="partner-reject-reason"
          style={{
            marginBottom: 7,
            display: 'block',
            fontSize: 'var(--text-meta-lg)',
            fontWeight: 'var(--weight-medium)',
            color: 'var(--text-muted)',
          }}
        >
          Reason
        </label>
        <input
          id="partner-reject-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this partner being rejected?"
          style={{
            marginBottom: 18,
            width: '100%',
            borderRadius: 'var(--radius-input)',
            border: '1px solid var(--border-hairline)',
            background: 'var(--surface-card)',
            paddingLeft: 11,
            paddingRight: 11,
            paddingTop: 7,
            paddingBottom: 7,
            fontFamily: 'inherit',
            fontSize: 'var(--text-body-md)',
            color: 'var(--text-strong)',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 7 }}>
          <Button variant="secondary" onClick={() => setRejecting(null)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!reason.trim() || reject.isPending}
            onClick={() => rejecting && reject.mutate({ id: rejecting, why: reason.trim() })}
          >
            Reject
          </Button>
        </div>
      </Dialog>
    </>
  );

  return {
    query,
    partners: query.data ?? [],
    approve: (id: string) => approve.mutate(id),
    startReject: (id: string) => setRejecting(id),
    isApproving: approve.isPending,
    dialogs,
  };
}
